/**
 * PolicyRule field renderers — set refs, workflows, relative target attribute, attributes.
 *
 * Uses searchable dropdown selectors (SetSelector, WorkflowSelector, SchemaAttributeSelector)
 * instead of modal-only PopupReferenceField for faster, fewer-click selection.
 */

import React from 'react';
import { Alert, Box, Checkbox, Group, Text } from '@mantine/core';
import { SetSelector } from '../../common/SetSelector';
import { WorkflowSelector } from '../../common/WorkflowSelector';
import { SchemaAttributeSelector } from '../../common/SchemaAttributeSelector';
import { AttributeSelector } from '../../common/AttributeSelector';
import { getEntityValueFromARef, type EntityRef } from '../../../types';
import { isARef } from '../../../utils/entityPropertyUtils';
import { extractReferenceId } from '../../../utils/referenceUtils';
import EntitlementsService from '../../../services/EntitlementsService';
import { store } from '../../../store/store';
import { getFieldOptionsForEntityType } from '../../../store/schemaSelectors';
import {
    buildFormAttributesFromTier2Attributes,
    buildFormAttributesFromTier2AttributeBindings,
    isEntitySetFacade,
} from '../../../types/EntityEntitlements';
import type { FieldAttribute } from './types';

export type PolicyRuleSetFieldKey = 'RequestorSet' | 'TargetSet' | 'TargetPostOpSet';

export interface PolicyRuleFieldRenderContext {
    attr: FieldAttribute;
    attrRef: EntityRef;
    name: string;
    label: string;
    value: unknown;
    isRequired: boolean;
    isFieldDisabled: boolean;
    formData: Record<string, any>;
    handleFieldChange: (fieldName: string, value: any) => void;
    handleRequestorSetChange: (setId: string | null) => void;
    handleTargetSetChange: (setId: string | null) => void;
    targetSetFilterScope: string | null;
    relativeTargetAttributeError: string | null;
    setRelativeTargetAttributeError: (error: string | null) => void;
    wrapWithFieldDebug: (fieldName: string, content: React.ReactNode, rendererKey?: string) => React.ReactNode;
}

interface PolicyRuleSetFieldConfig {
    formKey: PolicyRuleSetFieldKey;
    showOnlyValidTargets?: boolean;
    onSetChange: (ctx: PolicyRuleFieldRenderContext, setId: string | null, fieldName: string) => void;
    getError?: (ctx: PolicyRuleFieldRenderContext, setIdStr: string | null) => boolean;
    getDescription: (ctx: PolicyRuleFieldRenderContext, setIdStr: string | null) => React.ReactNode;
}

const POLICY_RULE_SET_FIELD_CONFIG: Record<PolicyRuleSetFieldKey, PolicyRuleSetFieldConfig> = {
    RequestorSet: {
        formKey: 'RequestorSet',
        onSetChange: (ctx, setId) => ctx.handleRequestorSetChange(setId),
        getError: (ctx, setIdStr) =>
            !!getEntityValueFromARef('RelativeTargetAttribute', ctx.formData) && !!setIdStr,
        getDescription: (ctx, setIdStr) =>
            getEntityValueFromARef('RelativeTargetAttribute', ctx.formData) && setIdStr
                ? 'Error: Cannot be used together with Target Relative Attribute'
                : 'Optional: Set defining who can perform the action. Cannot be used together with Target Relative Attribute.',
    },
    TargetSet: {
        formKey: 'TargetSet',
        showOnlyValidTargets: true,
        onSetChange: (ctx, setId) => ctx.handleTargetSetChange(setId),
        getDescription: () =>
            'Set defining what entities are affected - determines entity type for attributes (shows sets with FilterScope, including system sets)',
    },
    TargetPostOpSet: {
        formKey: 'TargetPostOpSet',
        onSetChange: (ctx, setId, fieldName) => ctx.handleFieldChange(fieldName, setId || ''),
        getDescription: () => 'Optional: Set to use after the operation completes',
    },
};

function resolveSetId(
    ctx: PolicyRuleFieldRenderContext,
    formKey: PolicyRuleSetFieldKey
): string | null {
    const raw =
        getEntityValueFromARef(formKey, ctx.formData)
        ?? getEntityValueFromARef(ctx.name, ctx.formData)
        ?? ctx.value;
    const id = extractReferenceId(raw) ?? raw;
    if (!id || !String(id).trim()) return null;
    return String(id).trim();
}

export function renderPolicyRuleSetField(
    setKey: PolicyRuleSetFieldKey,
    ctx: PolicyRuleFieldRenderContext
): React.ReactNode {
    const config = POLICY_RULE_SET_FIELD_CONFIG[setKey];
    const setIdStr = resolveSetId(ctx, config.formKey);

    return ctx.wrapWithFieldDebug(
        ctx.name,
        (
            <Box key={ctx.name} mb="md">
                <SetSelector
                    value={setIdStr}
                    onChange={(setId) => config.onSetChange(ctx, setId, ctx.name)}
                    label={ctx.label}
                    required={ctx.isRequired}
                    disabled={ctx.isFieldDisabled}
                    showOnlyValidTargets={config.showOnlyValidTargets}
                    error={config.getError?.(ctx, setIdStr)}
                    description={config.getDescription(ctx, setIdStr)}
                    allowClear={!ctx.isRequired}
                />
            </Box>
        ),
        setKey
    );
}

function renderPolicyRuleWorkflowField(
    workflowKey: 'RequestWorkflow' | 'AuthorizationWorkflow',
    workflowKind: 'Request' | 'Authorization',
    ctx: PolicyRuleFieldRenderContext
): React.ReactNode {
    const workflowId = extractReferenceId(getEntityValueFromARef(ctx.name, ctx.formData) ?? ctx.value);

    return ctx.wrapWithFieldDebug(
        ctx.name,
        (
            <Box key={ctx.name} mb="md">
                <WorkflowSelector
                    workflowKind={workflowKind}
                    value={workflowId ?? null}
                    onChange={(id) => ctx.handleFieldChange(ctx.name, id ?? '')}
                    label={ctx.label}
                    required={ctx.isRequired}
                    disabled={ctx.isFieldDisabled}
                />
            </Box>
        ),
        workflowKey
    );
}

function renderPolicyRuleRelativeTargetAttributeField(ctx: PolicyRuleFieldRenderContext): React.ReactNode {
    const relativeAttrValue =
        ctx.value == null || ctx.value === '' ? null : String(ctx.value);
    const requestorSetIdForRelative = extractReferenceId(
        getEntityValueFromARef('RequestorSet', ctx.formData)
    );

    const validateRelativeAttribute = (attrValue: string) => {
        if (!attrValue) {
            ctx.setRelativeTargetAttributeError(null);
            return;
        }
        if (!ctx.targetSetFilterScope) return;

        try {
            const normalizedAttr = attrValue.trim().toLowerCase();
            if (normalizedAttr === 'id') {
                ctx.setRelativeTargetAttributeError(null);
                return;
            }
            const state = store.getState();
            const fromRedux = getFieldOptionsForEntityType(state, ctx.targetSetFilterScope);
            const cachedTier2ForScope =
                EntitlementsService.getCachedEntityEntitlementForSchemaEntity(ctx.targetSetFilterScope);
            const fieldOptions =
                fromRedux.length > 0
                    ? fromRedux
                    : isEntitySetFacade(cachedTier2ForScope)
                        ? buildFormAttributesFromTier2Attributes(cachedTier2ForScope?.Attributes)
                        : buildFormAttributesFromTier2AttributeBindings(
                            cachedTier2ForScope?.AttributeBindings
                        ) ?? buildFormAttributesFromTier2Attributes(cachedTier2ForScope?.Attributes);
            if (fieldOptions.length === 0) return;
            const attributeExists = fieldOptions.some((attr) => {
                const attrName = attr.name?.toLowerCase();
                const attrId = attr.id?.toLowerCase();
                const selectedAttr = attrValue.toLowerCase();
                return attrName === selectedAttr || attrId === selectedAttr;
            });
            if (!attributeExists) {
                ctx.setRelativeTargetAttributeError(
                    `The selected attribute "${attrValue}" does not exist for entity type "${ctx.targetSetFilterScope}". Please select a different attribute.`
                );
            } else {
                ctx.setRelativeTargetAttributeError(null);
            }
        } catch (err) {
            console.error('Error validating attribute:', err);
            ctx.setRelativeTargetAttributeError(null);
        }
    };

    const descriptionText = requestorSetIdForRelative
        ? 'Cannot be used with Requestor Set — clear Requestor Set to use Relative Target Attribute'
        : !ctx.targetSetFilterScope
            ? 'Select a Target Set first to choose reference attributes'
            : relativeAttrValue && getEntityValueFromARef('RequestorSet', ctx.formData)
                ? 'Error: Cannot be used together with Requestor Set'
                : ctx.relativeTargetAttributeError
                    ? "Error: Attribute does not exist for the selected Target Set's entity type"
                    : 'Optional: Reference attribute of the target (only reference attributes shown). Cannot be used together with Requestor Set.';

    return ctx.wrapWithFieldDebug(
        ctx.name,
        (
            <Box key={ctx.name} mb="md">
                <SchemaAttributeSelector
                    value={relativeAttrValue}
                    onChange={(attrValue) => {
                        ctx.handleFieldChange(ctx.name, attrValue ?? '');
                        if (attrValue) ctx.handleFieldChange('RequestorSet', null);
                        if (!attrValue) {
                            ctx.setRelativeTargetAttributeError(null);
                        } else {
                            validateRelativeAttribute(attrValue);
                        }
                    }}
                    label={ctx.label}
                    disabled={ctx.isFieldDisabled || !ctx.targetSetFilterScope || !!requestorSetIdForRelative}
                    entityType={ctx.targetSetFilterScope || undefined}
                    filterReferenceOnly={true}
                    error={
                        !!relativeAttrValue
                        && (!!requestorSetIdForRelative || !!ctx.relativeTargetAttributeError)
                    }
                    description={descriptionText}
                />
                {ctx.relativeTargetAttributeError && (
                    <Alert color="red" mt="xs">
                        {ctx.relativeTargetAttributeError}
                    </Alert>
                )}
            </Box>
        ),
        'RelativeTargetAttribute'
    );
}

function renderPolicyRuleAttributesField(ctx: PolicyRuleFieldRenderContext): React.ReactNode {
    const allAttributes = getEntityValueFromARef('AllAttributes', ctx.formData) !== false;

    return ctx.wrapWithFieldDebug(
        ctx.name,
        (
            <Box key={ctx.name} mb="md">
                <Group align="center" gap="md" mb="md">
                    <Checkbox
                        checked={allAttributes}
                        onChange={(e) => ctx.handleFieldChange('AllAttributes', e.currentTarget.checked)}
                        disabled={ctx.isFieldDisabled}
                        label="Apply to All Attributes"
                    />
                    <Text size="sm" c="dimmed">
                        {allAttributes
                            ? 'Policy rule will apply to all attributes'
                            : 'Select specific attributes below'}
                    </Text>
                </Group>
                <AttributeSelector
                    value={
                        allAttributes
                            ? null
                            : ctx.value == null || ctx.value === ''
                                ? null
                                : Array.isArray(ctx.value)
                                    ? (ctx.value as unknown[]).map((v) => String(v))
                                    : [String(ctx.value)]
                    }
                    onChange={(newValue) => {
                        const arrayValue = Array.isArray(newValue)
                            ? newValue
                            : newValue
                                ? [newValue]
                                : null;
                        ctx.handleFieldChange(ctx.name, arrayValue);
                    }}
                    label={ctx.label}
                    disabled={ctx.isFieldDisabled || allAttributes || !ctx.targetSetFilterScope}
                    multiValued={true}
                    entityType={ctx.targetSetFilterScope || undefined}
                    lockAfterSelection={false}
                    helperText={
                        !ctx.targetSetFilterScope
                            ? 'Select a Target Set first to choose attributes'
                            : allAttributes
                                ? 'All attributes are selected - disable this toggle to choose specific attributes'
                                : 'Select which specific attributes this policy rule applies to'
                    }
                />
            </Box>
        ),
        'Attributes'
    );
}

/**
 * Render a PolicyRule-specific field, or null if this attribute is not handled here.
 */
export function tryRenderPolicyRuleField(
    attrRef: EntityRef,
    ctx: PolicyRuleFieldRenderContext
): React.ReactNode | null {
    if (isARef(attrRef, 'RequestorSet')) {
        return renderPolicyRuleSetField('RequestorSet', ctx);
    }
    if (isARef(attrRef, 'TargetSet')) {
        return renderPolicyRuleSetField('TargetSet', ctx);
    }
    if (isARef(attrRef, 'TargetPostOpSet')) {
        return renderPolicyRuleSetField('TargetPostOpSet', ctx);
    }
    if (isARef(attrRef, 'RequestWorkflow')) {
        return renderPolicyRuleWorkflowField('RequestWorkflow', 'Request', ctx);
    }
    if (isARef(attrRef, 'AuthorizationWorkflow')) {
        return renderPolicyRuleWorkflowField('AuthorizationWorkflow', 'Authorization', ctx);
    }
    if (isARef(attrRef, 'RelativeTargetAttribute')) {
        return renderPolicyRuleRelativeTargetAttributeField(ctx);
    }
    if (isARef(attrRef, 'Attributes')) {
        return renderPolicyRuleAttributesField(ctx);
    }
    return null;
}
